import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, NgZone, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { Cible, CiblePayload, CiblesService } from '../../../services/cibles.service';

@Component({
  selector: 'app-cibles',
  imports: [CommonModule, FormsModule],
  templateUrl: './cibles.component.html',
  styleUrl: './cibles.component.scss'
})
export class CiblesComponent implements OnInit {
  protected pageMode: 'creer' | 'modifier' | 'supprimer' = 'supprimer';
  protected cibles: Cible[] = [];
  protected selectedId = '';
  protected nouveauNom = '';
  protected nomEdition = '';
  protected loading = false;
  protected loadingList = false;
  protected deletingCibleId: number | null = null;
  protected success = '';
  protected error = '';

  constructor(
    private readonly ciblesService: CiblesService,
    private readonly router: Router,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.syncPageMode();
    this.router.events.subscribe((event: unknown) => {
      if (event instanceof NavigationEnd) {
        this.syncPageMode();
        this.loadCibles();
      }
    });
    this.loadCibles();
  }

  protected get showCreateSection(): boolean {
    return this.pageMode === 'creer';
  }

  protected get showEditSection(): boolean {
    return this.pageMode === 'modifier';
  }

  protected get showListSection(): boolean {
    return this.pageMode === 'modifier' || this.pageMode === 'supprimer';
  }

  protected get showDeleteActionsInTable(): boolean {
    return this.pageMode === 'supprimer';
  }

  private syncPageMode(): void {
    const lastPart = this.router.url.split('/').pop() ?? '';
    if (['creer', 'modifier', 'supprimer'].includes(lastPart)) {
      this.pageMode = lastPart as 'creer' | 'modifier' | 'supprimer';
    } else {
      this.pageMode = 'supprimer';
    }
  }

  protected createCible(): void {
    if (!this.nouveauNom.trim()) {
      this.error = 'Le nom est requis.';
      return;
    }

    this.loading = true;
    this.error = '';
    this.success = '';

    const payload: CiblePayload = {
      nom: this.nouveauNom.trim(),
    };

    this.zone.run(() => {
      this.ciblesService.create(payload).subscribe({
        next: () => {
          this.success = 'Cible créée avec succès.';
          this.nouveauNom = '';
          this.loadCibles();
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors de la création: ${this.getErrorMessage(err)}`;
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  protected onSelectChange(): void {
    const selected = this.cibles.find((cible) => cible.id === Number(this.selectedId));
    this.nomEdition = selected?.nom ?? '';
  }

  protected updateCible(): void {
    if (!this.selectedId) {
      this.error = 'Veuillez sélectionner une cible.';
      return;
    }
    if (!this.nomEdition.trim()) {
      this.error = 'Le nom est requis.';
      return;
    }

    this.loading = true;
    this.error = '';
    this.success = '';

    const payload: CiblePayload = {
      nom: this.nomEdition.trim(),
    };

    this.zone.run(() => {
      this.ciblesService.update(Number(this.selectedId), payload).subscribe({
        next: () => {
          this.success = 'Cible modifiée avec succès.';
          this.loadCibles();
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors de la modification: ${this.getErrorMessage(err)}`;
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  protected deleteCibleRow(cible: Cible): void {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer la cible "${cible.nom}" ?`)) {
      return;
    }

    this.deletingCibleId = cible.id;
    this.error = '';
    this.success = '';

    this.zone.run(() => {
      this.ciblesService.delete(cible.id).subscribe({
        next: () => {
          this.success = 'Cible supprimée avec succès.';
          this.loadCibles();
          if (this.selectedId === String(cible.id)) {
            this.selectedId = '';
            this.nomEdition = '';
          }
          this.deletingCibleId = null;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors de la suppression: ${this.getErrorMessage(err)}`;
          this.deletingCibleId = null;
          this.cdr.markForCheck();
        }
      });
    });
  }

  private loadCibles(): void {
    this.loadingList = true;
    this.error = '';

    this.zone.run(() => {
      this.ciblesService.list().subscribe({
        next: (cibles: Cible[]) => {
          this.cibles = cibles;
          if (this.selectedId) {
            this.onSelectChange();
          }
          this.loadingList = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors du chargement: ${this.getErrorMessage(err)}`;
          this.loadingList = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  private getErrorMessage(err: HttpErrorResponse): string {
    if (typeof err.error === 'string' && err.error.trim()) {
      return err.error;
    }

    if (err.error?.detail) {
      return err.error.detail;
    }

    if (err.error?.message) {
      return err.error.message;
    }

    return err.statusText || 'Erreur inconnue';
  }
}
